import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api, { getErrorMessage } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Users, DollarSign, Clock, Calendar, ChevronLeft, ChevronRight,
  Trash2, UserPlus, UserMinus, BarChart3, AlertTriangle, Baby, CalendarDays, Receipt, Building2
} from 'lucide-react';
import AdminInstructorsTab from './admin/AdminInstructorsTab';
import AdminChildrenTab from './admin/AdminChildrenTab';
import AdminPayrollTab from './admin/AdminPayrollTab';
import AdminFundingTab from './admin/AdminFundingTab';
import AdminSchedulesTab from './admin/AdminSchedulesTab';
import AdminUsersTab from './admin/AdminUsersTab';
import AdminExpensesTab from './admin/AdminExpensesTab';
import AdminFinancialTab from './admin/AdminFinancialTab';
import AdminFacilitiesTab from './admin/AdminFacilitiesTab';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function getDaysInMonth(year, month) { return new Date(year, month + 1, 0).getDate(); }
function getFirstDayOfMonth(year, month) { return new Date(year, month, 1).getDay(); }

export default function AdminDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState('instructors');
  const [instructors, setInstructors] = useState([]);
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [hours, setHours] = useState([]);
  const [funding, setFunding] = useState([]);
  const [calendarData, setCalendarData] = useState({});
  const [stats, setStats] = useState({});
  const [allChildren, setAllChildren] = useState([]);
  const [allSchedules, setAllSchedules] = useState([]);
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());

  // Dialog states
  const [showPayRate, setShowPayRate] = useState(false);
  const [newRate, setNewRate] = useState('');
  const [showAssignChild, setShowAssignChild] = useState(false);
  const [showDeleteSession, setShowDeleteSession] = useState(null);
  const [showRemoveInstructor, setShowRemoveInstructor] = useState(null);
  const [showDeleteChild, setShowDeleteChild] = useState(null);
  const [adminNote, setAdminNote] = useState('');

  useEffect(() => { fetchInstructors(); fetchAllChildren(); fetchAllSchedules(); }, []);

  const fetchInstructors = async () => {
    try { const res = await api.get('/admin/instructors'); setInstructors(res.data); } catch { toast.error('Failed to load instructors'); }
  };
  const fetchAllChildren = async () => {
    try { const res = await api.get('/admin/children'); setAllChildren(res.data); } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); }
  };
  const fetchAllSchedules = async () => {
    try { const res = await api.get('/admin/schedules'); setAllSchedules(res.data); } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); }
  };

  const selectInstructor = async (inst) => {
    setSelectedInstructor(inst);
    try {
      const [h, f, c, s] = await Promise.all([
        api.get(`/hours?instructor_id=${inst.user_id}`),
        api.get(`/funding?instructor_id=${inst.user_id}`),
        api.get(`/admin/calendar?instructor_id=${inst.user_id}`),
        api.get(`/admin/session-stats?instructor_id=${inst.user_id}`),
      ]);
      setHours(h.data); setFunding(f.data); setCalendarData(c.data.calendar || {}); setStats(s.data);
    } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); }
  };

  const refreshInstructorData = () => { if (selectedInstructor) selectInstructor(selectedInstructor); };

  const updatePayRate = async () => {
    if (!selectedInstructor) return;
    try { await api.put(`/admin/instructors/${selectedInstructor.user_id}/pay-rate`, { pay_rate: parseFloat(newRate) }); toast.success('Pay rate updated'); setShowPayRate(false); fetchInstructors(); refreshInstructorData(); } catch { toast.error('Failed to update'); }
  };
  const handleHourAction = async (logId, status) => {
    try { await api.put(`/hours/${logId}`, { status, admin_notes: adminNote }); toast.success(`Hour ${status}`); setAdminNote(''); refreshInstructorData(); } catch { toast.error('Failed'); }
  };
  const deleteHour = async (logId) => {
    try { await api.delete(`/admin/hours/${logId}`); toast.success('Hour removed'); refreshInstructorData(); } catch { toast.error('Failed'); }
  };
  const handleFundingAction = async (reqId, status) => {
    try { await api.put(`/funding/${reqId}`, { status, admin_notes: adminNote }); toast.success(`Request ${status}`); setAdminNote(''); refreshInstructorData(); } catch { toast.error('Failed'); }
  };
  const assignChild = async (childId, action) => {
    if (!selectedInstructor) return;
    try { await api.put(`/admin/instructors/${selectedInstructor.user_id}/children`, { action, child_id: childId }); toast.success(action === 'add' ? 'Child assigned' : 'Child removed'); fetchInstructors(); refreshInstructorData(); } catch { toast.error('Failed'); }
  };
  const deleteSession = async (sessionId) => {
    try { await api.delete(`/admin/sessions/${sessionId}`); toast.success('Session deleted'); setShowDeleteSession(null); refreshInstructorData(); } catch (err) { toast.error(getErrorMessage(err, 'Failed')); }
  };
  const removeInstructor = async () => {
    if (!showRemoveInstructor) return;
    try { await api.delete(`/admin/instructors/${showRemoveInstructor.user_id}`); toast.success(`${showRemoveInstructor.name || showRemoveInstructor.email} removed`); setShowRemoveInstructor(null); if (selectedInstructor?.user_id === showRemoveInstructor.user_id) setSelectedInstructor(null); fetchInstructors(); } catch (err) { toast.error(getErrorMessage(err, 'Failed to remove')); }
  };
  const deleteChild = async () => {
    if (!showDeleteChild) return;
    try { await api.delete(`/admin/children/${showDeleteChild.child_id}`); toast.success(`${showDeleteChild.name || 'Child'} and all data deleted`); setShowDeleteChild(null); fetchAllChildren(); fetchInstructors(); } catch (err) { toast.error(getErrorMessage(err, 'Failed to delete')); }
  };
  const markEntryCompleted = async (scheduleId, entryId) => {
    try { await api.put(`/schedules/${scheduleId}/entries/${entryId}/complete`); toast.success('Marked as completed'); fetchAllSchedules(); } catch { toast.error('Failed to mark complete'); }
  };

  // Calendar rendering for instructor calendar tab
  const daysInMonth = getDaysInMonth(calYear, calMonth);
  const firstDay = getFirstDayOfMonth(calYear, calMonth);
  const calendarCells = [];
  for (let i = 0; i < firstDay; i++) calendarCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarCells.push(d);

  const pendingHours = hours.filter(h => h.status === 'pending');
  const pendingFunding = funding.filter(f => f.status === 'pending');
  const payRate = selectedInstructor?.pay_rate || 37.5;

  const sortedCalendarEntries = useMemo(() =>
    Object.entries(calendarData).sort((a, b) => b[0].localeCompare(a[0])),
    [calendarData]
  );

  const tabs = [
    { id: 'instructors', label: 'Instructors', icon: Users },
    { id: 'children', label: 'Children', icon: Baby },
    { id: 'payroll', label: 'Payroll', icon: DollarSign },
    { id: 'funding', label: 'Funding', icon: DollarSign },
    { id: 'expenses', label: 'Expenses', icon: Receipt },
    { id: 'financial', label: 'Financial', icon: BarChart3 },
    { id: 'schedules', label: 'Schedules', icon: CalendarDays },
    { id: 'facilities', label: 'Facilities', icon: Building2 },
    { id: 'users', label: 'Users', icon: UserPlus },
  ];

  return (
    <div className="max-w-6xl mx-auto animate-fadeIn" data-testid="admin-dashboard">
      <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-800 mb-2" style={{ fontFamily: 'Nunito' }}>Admin Dashboard</h1>
      <p className="text-slate-500 mb-6">Manage instructors, hours, funding, and sessions</p>

      {/* Tabs */}
      <div className="relative mb-6">
        <div className="flex gap-2 overflow-x-auto pb-4 -mx-4 px-4 pr-8 scrollbar-hide">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm whitespace-nowrap transition-all min-h-[48px] shrink-0 ${tab === t.id ? 'bg-[#1E1B4B] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid={`tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
              {t.id === 'hours' && pendingHours.length > 0 && <Badge className="bg-red-500 text-white text-xs px-1.5 rounded-full ml-1">{pendingHours.length}</Badge>}
              {t.id === 'funding' && pendingFunding.length > 0 && <Badge className="bg-red-500 text-white text-xs px-1.5 rounded-full ml-1">{pendingFunding.length}</Badge>}
            </button>
          ))}
        </div>
        <div className="absolute right-0 top-0 bottom-4 w-8 bg-gradient-to-l from-[#F0FDFA] to-transparent pointer-events-none lg:hidden" />
      </div>

      {/* Instructor selector for tabs that need it */}
      {['funding'].includes(tab) && !selectedInstructor && (
        <Card className="rounded-3xl border-dashed border-2 border-slate-200 mb-6">
          <CardContent className="p-8 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-lg font-bold text-slate-400">Select an instructor first</p>
            <Button className="mt-4 rounded-full bg-[#22D3EE]" onClick={() => setTab('instructors')}>Go to Instructors</Button>
          </CardContent>
        </Card>
      )}

      {/* Tab content */}
      {tab === 'instructors' && (
        <AdminInstructorsTab
          instructors={instructors}
          selectedInstructor={selectedInstructor}
          user={user}
          onSelect={selectInstructor}
          onEditRate={(inst) => { setSelectedInstructor(inst); setNewRate(String(inst.pay_rate || 37.5)); setShowPayRate(true); }}
          onManageChildren={(inst) => { selectInstructor(inst); setShowAssignChild(true); }}
          onRemove={setShowRemoveInstructor}
        />
      )}

      {tab === 'children' && (
        <AdminChildrenTab allChildren={allChildren} onDeleteChild={setShowDeleteChild} />
      )}

      {tab === 'payroll' && (
        <AdminPayrollTab instructors={instructors} onSelectInstructor={selectInstructor} />
      )}

      {tab === 'funding' && selectedInstructor && (
        <AdminFundingTab
          selectedInstructor={selectedInstructor}
          funding={funding}
          onApprove={(id) => handleFundingAction(id, 'approved')}
          onReject={(id) => handleFundingAction(id, 'rejected')}
        />
      )}

      {tab === 'schedules' && (
        <AdminSchedulesTab allSchedules={allSchedules} onMarkComplete={markEntryCompleted} onRefresh={fetchAllSchedules} />
      )}

      {tab === 'users' && <AdminUsersTab />}

      {tab === 'facilities' && <AdminFacilitiesTab />}

      {tab === 'expenses' && <AdminExpensesTab />}

      {tab === 'financial' && <AdminFinancialTab />}

      {/* Dialogs */}
      <Dialog open={showPayRate} onOpenChange={setShowPayRate}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Nunito' }}>Set Pay Rate</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-500">Set hourly rate for {selectedInstructor?.name}</p>
            <div className="relative">
              <DollarSign className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
              <Input data-testid="pay-rate-input" className="pl-10 rounded-xl" type="number" step="0.5" value={newRate} onChange={e => setNewRate(e.target.value)} />
            </div>
            <Button onClick={updatePayRate} className="w-full rounded-full bg-[#22D3EE] hover:bg-[#06B6D4] font-bold" data-testid="save-pay-rate-btn">Save Rate</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showAssignChild} onOpenChange={setShowAssignChild}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Nunito' }}>Manage {selectedInstructor?.name}'s Students</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2 max-h-[60vh] overflow-auto">
            <p className="text-xs font-semibold text-slate-500 uppercase">Current Students</p>
            {(selectedInstructor?.children || []).map(c => (
              <div key={c.child_id} className="flex items-center justify-between bg-green-50 rounded-xl p-3">
                <span className="font-medium text-slate-700 text-sm">{c.name}</span>
                <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600 gap-1" onClick={() => assignChild(c.child_id, 'remove')} data-testid={`remove-child-${c.child_id}`}><UserMinus className="w-4 h-4" /> Remove</Button>
              </div>
            ))}
            <p className="text-xs font-semibold text-slate-500 uppercase mt-4">Unassigned Children</p>
            {allChildren.filter(c => c.instructor_id !== selectedInstructor?.user_id).map(c => (
              <div key={c.child_id} className="flex items-center justify-between bg-slate-50 rounded-xl p-3">
                <span className="font-medium text-slate-700 text-sm">{c.name}</span>
                <Button size="sm" variant="ghost" className="text-green-500 hover:text-green-700 gap-1" onClick={() => assignChild(c.child_id, 'add')} data-testid={`assign-child-${c.child_id}`}><UserPlus className="w-4 h-4" /> Assign</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!showDeleteSession} onOpenChange={() => setShowDeleteSession(null)}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader><DialogTitle className="text-red-600 flex items-center gap-2" style={{ fontFamily: 'Nunito' }}><AlertTriangle className="w-5 h-5" /> Delete Session</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-600">This will permanently delete <strong>Session #{showDeleteSession?.session_number}</strong> and all associated videos, assessments, and hour logs.</p>
            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" className="rounded-xl font-bold" onClick={() => setShowDeleteSession(null)}>Cancel</Button>
              <Button className="rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold gap-2" onClick={() => deleteSession(showDeleteSession?.session_id)} data-testid="confirm-admin-delete-session"><Trash2 className="w-4 h-4" /> Delete</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!showRemoveInstructor} onOpenChange={() => setShowRemoveInstructor(null)}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader><DialogTitle className="text-red-600 flex items-center gap-2" style={{ fontFamily: 'Nunito' }}><AlertTriangle className="w-5 h-5" /> Remove Instructor</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-600">This will permanently remove <strong>{showRemoveInstructor?.name || showRemoveInstructor?.email}</strong> and unassign all their students.</p>
            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" className="rounded-xl font-bold" onClick={() => setShowRemoveInstructor(null)} data-testid="cancel-remove-instructor">Cancel</Button>
              <Button className="rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold gap-2" onClick={removeInstructor} data-testid="confirm-remove-instructor"><UserMinus className="w-4 h-4" /> Remove</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!showDeleteChild} onOpenChange={() => setShowDeleteChild(null)}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader><DialogTitle className="text-red-600 flex items-center gap-2" style={{ fontFamily: 'Nunito' }}><AlertTriangle className="w-5 h-5" /> Delete Child</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-600">This will permanently delete <strong>{showDeleteChild?.name || 'this child'}</strong> and all their sessions, videos, assessments, criteria, and reports.</p>
            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" className="rounded-xl font-bold" onClick={() => setShowDeleteChild(null)} data-testid="cancel-delete-child">Cancel</Button>
              <Button className="rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold gap-2" onClick={deleteChild} data-testid="confirm-delete-child"><Trash2 className="w-4 h-4" /> Delete</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
